import {
  type Course,
  type CourseCreateRequest,
  type CourseDetail,
  type CourseTest,
  type CourseTestCreateRequest,
  type CourseTestDetail,
  type CourseTestUpdateRequest,
  type CourseUpdateRequest,
  type CoursesListQuerySchema,
  type DiscountGrantDetail,
  type DiscountGrantsListQuerySchema,
  type DiscountRule,
  type DiscountRuleCreateRequest,
  type DiscountRuleUpdateRequest,
  type DiscountRulesListQuerySchema,
  type DiscountSource,
  type Lesson,
  type LessonCreateRequest,
  type LessonDetail,
  type LessonUpdateRequest,
  type Paginated,
  type TestAttemptDetail,
  type TestAttemptsListQuerySchema,
  type TestQuestionAuthoring,
  type TestQuestionCreateRequest,
  type TestQuestionUpdateRequest,
  type TestsListQuerySchema,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';

/** The contract's own INPUT shape (U3) — never a hand-written copy of the filters. */
type CoursesParams = z.input<typeof CoursesListQuerySchema>;
type TestsParams = z.input<typeof TestsListQuerySchema>;
type RulesParams = z.input<typeof DiscountRulesListQuerySchema>;
type GrantsParams = z.input<typeof DiscountGrantsListQuerySchema>;
type AttemptsParams = z.input<typeof TestAttemptsListQuerySchema>;

/**
 * Which course a test write moves, so its cache entry can be named.
 *
 * Every test and question mutation carries it, because the api's answer does not
 * always: a `204` carries nothing at all, and a `DELETE` has no body to read the
 * course out of. `null` is a standalone test, which owns no course row.
 */
interface TestScope {
  courseId: string | null;
}

/**
 * What a rule write pays for, so the panels that list it can be named.
 *
 * Carried on every rule mutation for `TestScope`'s reason and one more: `source`
 * and `sourceId` are settable on **create only** (`DiscountRuleUpdateSchema`
 * carries neither), so an update's own argument cannot say what the rule points
 * at — only the caller, which already has the row on screen, can.
 */
interface RuleScope {
  source: DiscountSource | null;
  sourceId: string | null;
}

/**
 * The curriculum: what the platform teaches, and what finishing it is worth.
 *
 * ## One api for the whole module, not one per resource
 *
 * Courses, lessons, tests, questions, discount rules, grants and attempts are
 * seven collections and **one** operator workflow: author a course, give it a
 * test, decide what passing pays, then answer "why has this member not got
 * their discount?". The reactive edges cross all of them — a lesson write moves
 * a course's `lessonCount`, a test write moves its `testId`, a rule names a
 * course as its source — and one api's `invalidatesTags` cannot reach another's
 * cache (P17c). `violationsApi` and `notificationsApi` are the same shape for
 * the same reason.
 *
 * P22b1 wired courses and lessons and P22b2 the tests and their questions; the
 * money surfaces (P22b3) add their endpoints **here**, not beside it.
 *
 * ## A write returns the row and the screen still re-reads
 *
 * `POST`/`PATCH` answer with the course, and writing that straight into the
 * cache would save a round trip. Deliberately not done, for the reason every
 * other api in this portal records: rendering from a mutation response gives the
 * screen two sources for one row, and they disagree the moment a colleague saves
 * a second later.
 *
 * ## The reorder routes are `PUT …/order` and they take the WHOLE set
 *
 * `CoursesService.reorder` compares the submitted ids against every course that
 * exists and answers `course_reorder_incomplete` for anything else — there is no
 * anchored move here of the kind questions have. `courseReorderAvailability` is
 * what keeps a refused drag off the screen; this layer only carries the request.
 */
export const coursesApi = createApi({
  reducerPath: 'coursesApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: [
    'Courses',
    'Course',
    'Lessons',
    'Lesson',
    'Tests',
    'Test',
    'Questions',
    'Rules',
    'Rule',
    'Grants',
    'Attempts',
  ],
  endpoints: (builder) => ({
    /**
     * The course list.
     *
     * `CoursesService.list` goes through `paginateFilteredLocalized`, which
     * honours `sort`/`order` — so unlike the violations queue (P19b) this list
     * has real sortable headers, and the columns carry `sortKey`s.
     */
    listCourses: builder.query<Paginated<Course>, CoursesParams>({
      query: (params) => ({ url: '/admin/courses', method: 'GET', params }),
      providesTags: ['Courses'],
    }),

    /** One course with EVERY locale — what the ar/en tabs prefill from (W7). */
    course: builder.query<CourseDetail, string>({
      query: (id) => ({ url: `/admin/courses/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Course' as const, id }],
    }),

    createCourse: builder.mutation<CourseDetail, CourseCreateRequest>({
      query: (body) => ({ url: '/admin/courses', method: 'POST', body }),
      invalidatesTags: ['Courses'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceCourseChange(queryFulfilled.then(({ data }) => data.id));
      },
    }),

    updateCourse: builder.mutation<CourseDetail, { id: string; body: CourseUpdateRequest }>({
      query: ({ id, body }) => ({ url: `/admin/courses/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Course' as const, id },
        'Courses' as const,
      ],
      async onQueryStarted({ id }, { queryFulfilled }) {
        await announceCourseChange(queryFulfilled.then(() => id));
      },
    }),

    deleteCourse: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/courses/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, id) => [
        { type: 'Course' as const, id },
        'Courses' as const,
        'Lessons' as const,
      ],
      async onQueryStarted(id, { queryFulfilled }) {
        await announceCourseChange(queryFulfilled.then(() => id));
      },
    }),

    /** The whole new order, in one request. Positions are never sent. */
    reorderCourses: builder.mutation<void, readonly string[]>({
      query: (ids) => ({ url: '/admin/courses/order', method: 'PUT', body: { ids } }),
      invalidatesTags: ['Courses'],
      async onQueryStarted(_args, { queryFulfilled }) {
        // `null`, because a reorder moved the whole set and naming one member of
        // it would be a fact a subscriber then filtered on.
        await announceCourseChange(queryFulfilled.then(() => null));
      },
    }),

    /**
     * A course's lessons — **unpaged**, by design on the api side: forty lessons
     * is a table of contents, not a page of results.
     *
     * That is also what makes the lesson rail unconditionally draggable, where
     * the course list is not: the client always holds the whole set.
     */
    listLessons: builder.query<Lesson[], string>({
      query: (courseId) => ({ url: `/admin/courses/${courseId}/lessons`, method: 'GET' }),
      providesTags: (_result, _error, courseId) => [{ type: 'Lessons' as const, id: courseId }],
    }),

    /** One lesson with every locale — the body included (W7). */
    lesson: builder.query<LessonDetail, { courseId: string; id: string }>({
      query: ({ courseId, id }) => ({
        url: `/admin/courses/${courseId}/lessons/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'Lesson' as const, id }],
    }),

    createLesson: builder.mutation<LessonDetail, { courseId: string; body: LessonCreateRequest }>({
      query: ({ courseId, body }) => ({
        url: `/admin/courses/${courseId}/lessons`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { courseId }) => lessonTags(courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceLessonChange(queryFulfilled.then(() => courseId));
      },
    }),

    updateLesson: builder.mutation<
      LessonDetail,
      { courseId: string; id: string; body: LessonUpdateRequest }
    >({
      query: ({ courseId, id, body }) => ({
        url: `/admin/courses/${courseId}/lessons/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { courseId, id }) => [
        { type: 'Lesson' as const, id },
        ...lessonTags(courseId),
      ],
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceLessonChange(queryFulfilled.then(() => courseId));
      },
    }),

    deleteLesson: builder.mutation<void, { courseId: string; id: string }>({
      query: ({ courseId, id }) => ({
        url: `/admin/courses/${courseId}/lessons/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { courseId, id }) => [
        { type: 'Lesson' as const, id },
        ...lessonTags(courseId),
      ],
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceLessonChange(queryFulfilled.then(() => courseId));
      },
    }),

    reorderLessons: builder.mutation<void, { courseId: string; ids: readonly string[] }>({
      query: ({ courseId, ids }) => ({
        url: `/admin/courses/${courseId}/lessons/order`,
        method: 'PUT',
        body: { ids },
      }),
      invalidatesTags: (_result, _error, { courseId }) => lessonTags(courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceLessonChange(queryFulfilled.then(() => courseId));
      },
    }),

    /**
     * Every test — a course's, and the standalone ones a course cannot reach.
     *
     * No `defaultSort` is imposed by this layer, unlike the course list: the base
     * repository's fallback is the primary key **descending** (a UUIDv7, so
     * newest first), and unlike a curriculum a set of tests genuinely has no
     * order of its own. Newest-first is the right first impression here.
     */
    listTests: builder.query<Paginated<CourseTest>, TestsParams>({
      query: (params) => ({ url: '/admin/tests', method: 'GET', params }),
      providesTags: ['Tests'],
    }),

    /** One test with EVERY locale — what the ar/en tabs prefill from (W7). */
    test: builder.query<CourseTestDetail, string>({
      query: (id) => ({ url: `/admin/tests/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Test' as const, id }],
    }),

    createTest: builder.mutation<CourseTestDetail, CourseTestCreateRequest>({
      query: (body) => ({ url: '/admin/tests', method: 'POST', body }),
      invalidatesTags: (_result, _error, { courseId }) => testTags(courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    /**
     * `body` deliberately cannot carry `courseId` — `CourseTestUpdateSchema` does
     * not have the field, because moving a test between courses would re-point
     * every `course_completion` rule that named it. The `courseId` beside it is
     * the test's CURRENT course, carried only so the cache entry can be named.
     */
    updateTest: builder.mutation<
      CourseTestDetail,
      TestScope & { id: string; body: CourseTestUpdateRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/tests/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id, courseId }) => [
        { type: 'Test' as const, id },
        ...testTags(courseId),
      ],
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    deleteTest: builder.mutation<void, TestScope & { id: string }>({
      query: ({ id }) => ({ url: `/admin/tests/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id, courseId }) => [
        { type: 'Test' as const, id },
        { type: 'Questions' as const, id },
        ...testTags(courseId),
      ],
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    /**
     * A test's questions — **unpaged**, by design on the api side, and with the
     * answer key: this is the one route that serves `isCorrect`, and it is behind
     * `courses.read`.
     *
     * Unpaged is also what makes the question list unconditionally draggable,
     * exactly as it does for the lesson rail: the client always holds the whole
     * set, so a whole-set reorder is always expressible.
     */
    listQuestions: builder.query<TestQuestionAuthoring[], string>({
      query: (testId) => ({ url: `/admin/tests/${testId}/questions`, method: 'GET' }),
      providesTags: (_result, _error, testId) => [{ type: 'Questions' as const, id: testId }],
    }),

    createQuestion: builder.mutation<
      void,
      TestScope & { testId: string; body: TestQuestionCreateRequest }
    >({
      query: ({ testId, body }) => ({
        url: `/admin/tests/${testId}/questions`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_result, _error, { testId, courseId }) => questionTags(testId, courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    /**
     * `body.options`, when present, is the **WHOLE** option set and replaces what
     * is stored — see `TestQuestionOptionWriteSchema`. Nothing in this layer can
     * make that safe; `test-options.util.ts` is where it is made unrepresentable.
     */
    updateQuestion: builder.mutation<
      void,
      TestScope & { testId: string; questionId: string; body: TestQuestionUpdateRequest }
    >({
      query: ({ testId, questionId, body }) => ({
        url: `/admin/tests/${testId}/questions/${questionId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_result, _error, { testId, courseId }) => questionTags(testId, courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    deleteQuestion: builder.mutation<void, TestScope & { testId: string; questionId: string }>({
      query: ({ testId, questionId }) => ({
        url: `/admin/tests/${testId}/questions/${questionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, { testId, courseId }) => questionTags(testId, courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    /** The whole new order, in one request. Positions are never sent. */
    reorderQuestions: builder.mutation<
      void,
      TestScope & { testId: string; ids: readonly string[] }
    >({
      query: ({ testId, ids }) => ({
        url: `/admin/tests/${testId}/questions/order`,
        method: 'PUT',
        body: { ids },
      }),
      invalidatesTags: (_result, _error, { testId, courseId }) => questionTags(testId, courseId),
      async onQueryStarted({ courseId }, { queryFulfilled }) {
        await announceTestChange(queryFulfilled.then(() => courseId));
      },
    }),

    /**
     * The discount rules — what finishing a course or passing a test is worth.
     *
     * Its own resource prefix rather than a path under `admin/courses`, and that
     * is a routing fact rather than taste: `CoursesAdminController` owns
     * `@Get(':id')`, Nest matches in registration order, and a sibling word
     * there is read as a course id and answered **400** by a `ParseUUIDPipe`
     * (P22a). The same is true of grants and attempts.
     */
    listRules: builder.query<Paginated<DiscountRule>, RulesParams>({
      query: (params) => ({ url: '/admin/discount-rules', method: 'GET', params }),
      providesTags: ['Rules'],
    }),

    rule: builder.query<DiscountRule, string>({
      query: (id) => ({ url: `/admin/discount-rules/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'Rule' as const, id }],
    }),

    createRule: builder.mutation<DiscountRule, DiscountRuleCreateRequest>({
      query: (body) => ({ url: '/admin/discount-rules', method: 'POST', body }),
      invalidatesTags: ['Rules'],
      async onQueryStarted(_args, { queryFulfilled }) {
        await announceRuleChange(
          queryFulfilled.then(({ data }) => ({ source: data.source, sourceId: data.sourceId })),
        );
      },
    }),

    updateRule: builder.mutation<
      DiscountRule,
      RuleScope & { id: string; body: DiscountRuleUpdateRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/discount-rules/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Rule' as const, id },
        'Rules' as const,
      ],
      async onQueryStarted({ source, sourceId }, { queryFulfilled }) {
        await announceRuleChange(queryFulfilled.then(() => ({ source, sourceId })));
      },
    }),

    deleteRule: builder.mutation<void, RuleScope & { id: string }>({
      query: ({ id }) => ({ url: `/admin/discount-rules/${id}`, method: 'DELETE' }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Rule' as const, id },
        'Rules' as const,
      ],
      async onQueryStarted({ source, sourceId }, { queryFulfilled }) {
        await announceRuleChange(queryFulfilled.then(() => ({ source, sourceId })));
      },
    }),

    /**
     * The grants viewer — what members have actually earned (P22b4).
     *
     * Its own prefix, for `listRules`' routing reason. The rows carry `member`
     * resolved **server-side**, so nothing here looks an account up: that lookup
     * needs `members.read`, which the `courses.read` operator this screen exists
     * for need not hold (P19b).
     */
    listGrants: builder.query<Paginated<DiscountGrantDetail>, GrantsParams>({
      query: (params) => ({ url: '/admin/discount-grants', method: 'GET', params }),
      providesTags: ['Grants'],
    }),

    /**
     * Withdraws an unspent discount.
     *
     * The refusal is a **404**, not a conflict — see `courseErrorCopy`. The
     * caller re-reads rather than retrying, so this invalidates its list even
     * when it fails; that happens through the caller's own `refetch`, because a
     * rejected mutation runs no `invalidatesTags`.
     */
    revokeGrant: builder.mutation<DiscountGrantDetail, { id: string; userId: string }>({
      query: ({ id }) => ({ url: `/admin/discount-grants/${id}/revoke`, method: 'POST' }),
      invalidatesTags: ['Grants'],
      async onQueryStarted({ userId }, { queryFulfilled }) {
        await announceGrantRevoked(queryFulfilled.then(() => userId));
      },
    }),

    /** Every attempt members have made, with the member and the test named. */
    listAttempts: builder.query<Paginated<TestAttemptDetail>, AttemptsParams>({
      query: (params) => ({ url: '/admin/test-attempts', method: 'GET', params }),
      providesTags: ['Attempts'],
    }),

    /**
     * Gives a member their attempts at one test back.
     *
     * Keyed on **(test, member)** rather than on an attempt id, because that is
     * what the api clears — every attempt that member has made at that test, not
     * the row the action was invoked from.
     *
     * `{ attemptsCleared }` is an ad-hoc body rather than a contract type, so it
     * is declared here by hand. `0` is a legitimate success: the reset is an
     * idempotent soft delete.
     */
    resetAttempts: builder.mutation<
      { attemptsCleared: number },
      { testId: string; userId: string }
    >({
      query: ({ testId, userId }) => ({
        url: `/admin/test-attempts/${testId}/members/${userId}/reset`,
        method: 'POST',
      }),
      invalidatesTags: ['Attempts'],
      async onQueryStarted({ testId, userId }, { queryFulfilled }) {
        await announceAttemptsReset(queryFulfilled.then(() => ({ testId, userId })));
      },
    }),
  }),
});

/**
 * A lesson write moves the course's `lessonCount`, which the list column and the
 * detail header both draw — so it invalidates the course as well as its own
 * collection. Missing this is how a table shows "3 lessons" beside a rail of
 * four.
 */
function lessonTags(courseId: string) {
  return [
    { type: 'Lessons' as const, id: courseId },
    { type: 'Course' as const, id: courseId },
    'Courses' as const,
  ];
}

/**
 * A test write moves the course's `testId` — the fact the detail page's test
 * panel and the list's test column both draw — so it invalidates the course as
 * well as the test collection.
 *
 * A **standalone** test owns no course row, so there is nothing to re-read:
 * invalidating `Courses` for one would re-fetch the whole catalogue to learn that
 * none of it changed.
 */
function testTags(courseId: string | null) {
  if (courseId === null) {
    return ['Tests' as const];
  }

  return ['Tests' as const, { type: 'Course' as const, id: courseId }, 'Courses' as const];
}

/**
 * A question write moves the test's `questionCount`, which the tests list and the
 * detail header both draw — so it invalidates the test as well as its own
 * collection. Missing this is how a table shows "5 questions" beside a rail of
 * six.
 */
function questionTags(testId: string, courseId: string | null) {
  return [
    { type: 'Questions' as const, id: testId },
    { type: 'Test' as const, id: testId },
    ...testTags(courseId),
  ];
}

/**
 * Publishes once the write has actually landed, and **swallows the refusal**.
 *
 * The catch is deliberate and is the shape every api in this portal uses:
 * `onQueryStarted` has no caller, so a rejection escaping it becomes an
 * unhandled rejection — which a Vitest run reports as an error beside ten
 * passing tests and a non-zero exit (T7). The failure is already the caller's,
 * through `unwrap()`; there is simply nothing to announce, because nothing
 * happened.
 */
async function announceCourseChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.course.changed', courseId: await landed });
  } catch {
    // Refused. See above.
  }
}

async function announceLessonChange(landed: Promise<string>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.lesson.changed', courseId: await landed });
  } catch {
    // Refused. See above.
  }
}

async function announceTestChange(landed: Promise<string | null>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.test.changed', courseId: await landed });
  } catch {
    // Refused. See above.
  }
}

async function announceRuleChange(landed: Promise<RuleScope>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.rule.changed', ...(await landed) });
  } catch {
    // Refused. See above.
  }
}

async function announceGrantRevoked(landed: Promise<string>): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.grant.revoked', userId: await landed });
  } catch {
    // Refused. See above.
  }
}

async function announceAttemptsReset(
  landed: Promise<{ testId: string; userId: string }>,
): Promise<void> {
  try {
    EVENT_BUS.publish({ type: 'courses.attempts.reset', ...(await landed) });
  } catch {
    // Refused. See above.
  }
}

export const {
  useListCoursesQuery,
  useCourseQuery,
  useCreateCourseMutation,
  useUpdateCourseMutation,
  useDeleteCourseMutation,
  useReorderCoursesMutation,
  useListLessonsQuery,
  useLessonQuery,
  useCreateLessonMutation,
  useUpdateLessonMutation,
  useDeleteLessonMutation,
  useReorderLessonsMutation,
  useListTestsQuery,
  useTestQuery,
  useCreateTestMutation,
  useUpdateTestMutation,
  useDeleteTestMutation,
  useListQuestionsQuery,
  useCreateQuestionMutation,
  useUpdateQuestionMutation,
  useDeleteQuestionMutation,
  useReorderQuestionsMutation,
  useListRulesQuery,
  useRuleQuery,
  useCreateRuleMutation,
  useUpdateRuleMutation,
  useDeleteRuleMutation,
  useListGrantsQuery,
  useRevokeGrantMutation,
  useListAttemptsQuery,
  useResetAttemptsMutation,
} = coursesApi;
